import { useSidePanel } from '@/components/app/side-panel';
import { Button } from '@/components/ui/button';
import { useGetAd } from '@/features/meta-ads/api';
import { AdSidePanel } from '@/features/meta-ads/components/ad-side-panel';
import { AdsTable } from '@/features/meta-campaigns/components/ads-table';
import { useIsMobile } from '@/hooks/use-mobile';
import { CreateAdDialog } from '@/routes/_authed/ads/new/-components/create-ad-dialog';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { useEffect, useState } from 'react';

export const Route = createFileRoute(
  '/_authed/dashboard/l/$locationId/marketing/advertising/$id/'
)({
  validateSearch: (search: Record<string, unknown>): { ad?: string } => ({
    ad: typeof search.ad === 'string' ? search.ad : undefined,
  }),
  component: CampaignAdsPage,
});

/**
 * A campaign's own page on desktop: its ads with their metrics. Phones keep
 * the campaign in the side panel over the campaigns list.
 */
function CampaignAdsPage() {
  const { id, locationId } = Route.useParams();
  const { ad: adId } = Route.useSearch();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [createOpen, setCreateOpen] = useState(false);
  const { open } = useSidePanel();
  const { ad } = useGetAd(adId ?? '');
  // Phones keep main's flow: the campaign opens in the side panel over the
  // campaigns list, so this page hands its link to the list.
  useEffect(() => {
    if (isMobile)
      void navigate({
        to: '/dashboard/l/$locationId/marketing/advertising',
        params: { locationId },
        search: { campaign: id, ad: adId },
        replace: true,
      });
  }, [isMobile, id, adId, locationId, navigate]);
  useEffect(() => {
    if (isMobile) return;
    if (adId && ad?.metaCampaignId === id) {
      open(<AdSidePanel key={ad.id} ad={ad} />, { presentation: 'overlay' });
      void navigate({
        to: '/dashboard/l/$locationId/marketing/advertising/$id',
        params: { locationId, id },
        search: {},
        replace: true,
      });
    }
  }, [adId, ad, id, isMobile, locationId, open, navigate]);
  if (isMobile) return null;
  return (
    <>
      <title>Campaign Ads | Borrah</title>
      <div className="px-4 pt-4 lg:px-6">
        <Button variant="ghost" size="sm" asChild>
          <Link
            to="/dashboard/l/$locationId/marketing/advertising"
            params={{ locationId }}
          >
            <ArrowLeft className="size-4" />
            All campaigns
          </Link>
        </Button>
      </div>
      <AdsTable
        campaignId={id}
        onNewAd={() => {
          if (isMobile)
            void navigate({ to: '/ads/new', search: { campaignId: id } });
          else setCreateOpen(true);
        }}
      />
      <CreateAdDialog
        campaignId={id}
        open={createOpen}
        onOpenChange={setCreateOpen}
      />
    </>
  );
}
